import { useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import Router from "./routes";
import ThemeProvider from "./theme";
import StyledChart from "./components/chart/styles";
import ScrollToTop from "./components/ScrollToTop";
import { RootState } from "./store";
import Auth from "./Auth";
import { setBusinessTimezone } from "./utils/formatTime";

export default function App() {
  const user = useSelector((state: RootState) => state.appUser.user);
  // Every date shown and every "today" is the branch's (see formatTime.ts).
  // Set while rendering, so the screens below have it on their first render.
  setBusinessTimezone(user?.currentClient?.timezone);
  const [isActiveClient, setIsActiveClient] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  // Enhanced Backspace behavior:
  // - When no editable element is focused, Backspace navigates back
  // - When focused inside an editable element (input, textarea, or contenteditable), Backspace behaves normally
  useEffect(() => {
    const isEditable = (el: Element | null): boolean => {
      if (!el) return false;
      if (!(el instanceof HTMLElement)) return false;
      // contentEditable covers many rich editors
      if (el.isContentEditable) return true;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") {
        const input = el as HTMLInputElement | HTMLTextAreaElement;
        return !input.readOnly && !input.disabled;
      }
      // consider ARIA textbox role
      try {
        const role = (el as HTMLElement).getAttribute?.("role");
        if (role === "textbox") return true;
      } catch (e) {
        // ignore
      }
      return false;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Backspace") return;
      // Ignore modified combos (e.g., Ctrl+Backspace)
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const active = document.activeElement;
      if (isEditable(active)) {
        return; // allow normal behavior
      }
      // Not editing: trigger back navigation
      e.preventDefault();
      try {
        window.history.back();
      } catch (err) {
        // fallback to referrer or root
        window.location.href = document.referrer || "/";
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <ScrollToTop />
          <StyledChart />
          {isLoaded ? (
            <Router
              app={process.env.REACT_APP_APP}
              role={user?.role || ""}
              isBranch={!!user?.currentClient?.isBranch}
              isActiveClient={isActiveClient}
              access={user?.access || []}
            />
          ) : (
            <Auth
              setIsActiveClient={setIsActiveClient}
              setIsLoaded={setIsLoaded}
            />
          )}
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
